import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, DollarSign, ShoppingCart, TrendingUp, Plus, Edit2, Trash2, Eye, MoreVertical, Upload, Download, Search, Loader2, Globe, X, Video, Play, Languages, Truck, Car, Plane, Hotel, Utensils, Store, Check, CarTaxiFront, ClipboardList, RefreshCw, CheckCircle2, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/lib/auth-context';
import { useCurrency } from '@/lib/currency-context';
import api, { seller, products as productsApi, categories as categoriesApi, carRentals, hotelsFlights } from '@/lib/api';
import { formatDate, generateSlug } from '@/lib/utils';
import AIProductUpload from '@/components/products/AIProductUpload';
import { buildProductPath } from '@/lib/product-url';
import { toast } from 'sonner';

import CarRentalTab from '@/components/seller/CarRentalTab';
import HotelFlightTab from '@/components/seller/HotelFlightTab';
import RestaurantTab from '@/components/seller/RestaurantTab';
import SupermarketTab from '@/components/seller/SupermarketTab';
import MarketplaceIntegrationTab from '@/components/seller/MarketplaceIntegrationTab';
import TaxiLimousineTab from '@/components/seller/TaxiLimousineTab';
import ProductAIChatbox from '@/components/seller/ProductAIChatbox';
import TEMApiTab from '@/components/seller/TEMApiTab';
import TranslationCard from '@/components/seller/TranslationCard';
import SmartPagination from '@/components/SmartPagination';
import OrderDetailModal from '@/components/admin/dashboard/OrderDetailModal';
import { formatPaymentMethodLabel, resolveOrderPaymentMethod } from '@/lib/payment-methods';
import ProductDetailEditForm, { initProductEditForm, buildProductUpdatePayload } from '@/components/products/ProductDetailEditForm';
import SellerStoreProfileCard from '@/components/seller/SellerStoreProfileCard';

// Exchange rates for converting to USD (inverse of display rates)
const exchangeRatesToUSD = {
  'USD': 1,
  'EUR': 1.087,      // 1 EUR = 1.087 USD
  'GBP': 1.266,      // 1 GBP = 1.266 USD
  'TRY': 0.029,      // 1 TRY = 0.029 USD
  'JPY': 0.0067,     // 1 JPY = 0.0067 USD
  'CNY': 0.138,      // 1 CNY = 0.138 USD
  'INR': 0.012,      // 1 INR = 0.012 USD
  'AUD': 0.658,      // 1 AUD = 0.658 USD
  'CAD': 0.735,      // 1 CAD = 0.735 USD
  'BRL': 0.201,      // 1 BRL = 0.201 USD
  'MXN': 0.058,      // 1 MXN = 0.058 USD
  'RUB': 0.011,      // 1 RUB = 0.011 USD
  'SAR': 0.267,      // 1 SAR = 0.267 USD
  'AED': 0.272,      // 1 AED = 0.272 USD
};

const currencyLabels = {
  'USD': 'USD ($)',
  'EUR': 'EUR (€)',
  'GBP': 'GBP (£)',
  'TRY': 'TRY (₺)',
  'JPY': 'JPY (¥)',
  'CNY': 'CNY (¥)',
  'INR': 'INR (₹)',
  'AUD': 'AUD (A$)',
  'CAD': 'CAD (C$)',
  'BRL': 'BRL (R$)',
  'MXN': 'MXN (MX$)',
  'RUB': 'RUB (₽)',
  'SAR': 'SAR (SR)',
  'AED': 'AED',
};

// Available sizes (numeric and letter)
const availableSizes = [
  // Numeric sizes
  '34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '50', '51', '52', '53', '54', '55', '56',
  // Letter sizes
  'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'
];

// Predefined colors
const predefinedColors = [
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Red', value: '#FF0000' },
  { name: 'Blue', value: '#0000FF' },
  { name: 'Green', value: '#00FF00' },
  { name: 'Yellow', value: '#FFFF00' },
  { name: 'Orange', value: '#FFA500' },
  { name: 'Purple', value: '#800080' },
  { name: 'Pink', value: '#FFC0CB' },
  { name: 'Brown', value: '#A52A2A' },
  { name: 'Gray', value: '#808080' },
  { name: 'Navy', value: '#000080' },
  { name: 'Beige', value: '#F5F5DC' },
  { name: 'Gold', value: '#FFD700' },
  { name: 'Silver', value: '#C0C0C0' },
];

// Available countries for product availability
const availableCountries = [
  { code: 'ALL', name: 'Tüm Dünya / Worldwide', flag: '🌍' },
  { code: 'TR', name: 'Türkiye', flag: '🇹🇷' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
];

const SellerDashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { formatPrice: formatDisplayPrice } = useCurrency();
  
  // Get tab from URL or use seller_category
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab');
  
  // Determine initial tab based on seller_category or URL
  const getInitialTab = () => {
    if (tabFromUrl) return tabFromUrl;
    if (user?.seller_category) {
      const categoryToTab = {
        'products': 'products',
        'supermarket': 'supermarket',
        'cars': 'car-rentals',
        'car_rental': 'car-rentals',
        'car-rental': 'car-rentals',
        'hotels': 'hotel-flights',
        'flights': 'hotel-flights',
        'restaurants': 'restaurants',
        'taxi-limousine': 'taxi-limousine',
        'taxi_limousine': 'taxi-limousine',
        'taxi': 'taxi-limousine',
        'limousine': 'taxi-limousine',
      };
      return categoryToTab[user.seller_category] || 'products';
    }
    return 'products';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Check if user has access to a specific tab
  const hasAccessToTab = (tab) => {
    // Admin has access to all tabs
    if (user?.role === 'admin') return true;

    // Marketplace siparişleri: ürün sekmesine erişimi olan satıcılar
    if (tab === 'orders') {
      if (!user?.seller_category || user.seller_category === null || user.seller_category === undefined) {
        return true;
      }
      const categoryAccess = {
        'products': ['products'],
        'supermarket': ['supermarket'],
        'cars': ['car-rentals'],
        'car_rental': ['car-rentals'],
        'car-rental': ['car-rentals'],
        'hotels': ['hotel-flights'],
        'flights': ['hotel-flights'],
        'restaurants': ['restaurants'],
        'taxi-limousine': ['taxi-limousine'],
        'taxi_limousine': ['taxi-limousine'],
        'taxi': ['taxi-limousine'],
        'limousine': ['taxi-limousine'],
      };
      const allowedTabs = categoryAccess[user.seller_category] || ['products'];
      return allowedTabs.includes('products');
    }

    // If no seller_category set or undefined/null, allow all (legacy users)
    if (!user?.seller_category || user.seller_category === null || user.seller_category === undefined) {
      return true;
    }

    const categoryAccess = {
      'products': ['products'],
      'supermarket': ['supermarket'],
      'cars': ['car-rentals'],
      'car_rental': ['car-rentals'],
      'car-rental': ['car-rentals'],
      'hotels': ['hotel-flights'],
      'flights': ['hotel-flights'],
      'restaurants': ['restaurants'],
      'taxi-limousine': ['taxi-limousine'],
      'taxi_limousine': ['taxi-limousine'],
      'taxi': ['taxi-limousine'],
      'limousine': ['taxi-limousine'],
    };

    const allowedTabs = categoryAccess[user.seller_category] || ['products'];
    return allowedTabs.includes(tab);
  };
  
  // Count visible tabs for grid layout
  const visibleTabCount = ['products', 'supermarket', 'car-rentals', 'hotel-flights', 'restaurants', 'taxi-limousine'].filter(tab => hasAccessToTab(tab)).length;
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productPage, setProductPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showMarketPrice, setShowMarketPrice] = useState(false);
  const [marketPriceLoading, setMarketPriceLoading] = useState(false);
  const [marketPriceData, setMarketPriceData] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [countryMode, setCountryMode] = useState('all'); // 'all', 'include', 'exclude'
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [translatingProduct, setTranslatingProduct] = useState(null);
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [translationResult, setTranslationResult] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [exportingXml, setExportingXml] = useState(false);
  const [sellerOrderDetailOpen, setSellerOrderDetailOpen] = useState(false);
  const [sellerOrderDetailId, setSellerOrderDetailId] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    compare_price: '',
    tax_rate: '',
    shipping_cost: '',
    category_id: '',
    stock: '',
    brand: '',
    images: [''],
    videos: [''],
    is_featured: false,
    made_in_turkey: false, // Show "Made in Türkiye" badge
    // Physical properties
    weight: '',
    weight_unit: 'kg',
    dimensions: { width: '', height: '', depth: '' },
    dimension_unit: 'cm',
    volume: '',
    volume_unit: 'L',
    // Product variants
    sizes: [],
    colors: [],
    customColor: '', // for adding custom colors
    // Origin country
    origin_country: '',
    // Wholesale pricing tiers
    wholesale_tiers: []
  });

  // Available languages for translation
  const translationLanguages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchProducts(productPage, productSearchQuery);
    }
  }, [productPage, productSearchQuery]);

  const fetchProducts = async (page = 1, search = '') => {
    try {
      setProductsLoading(true);
      const params = { page, limit: 50 };
      if (search.trim()) params.search = search.trim();
      const productsRes = await seller.products(params);
      const productsData = productsRes.data?.products || productsRes.data || [];
      setProducts(productsData);
      setTotalProducts(productsRes.data?.total || 0);
      setTotalPages(productsRes.data?.total_pages || 0);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const refreshSellerOrders = async () => {
    try {
      setOrdersLoading(true);
      const ordersRes = await seller.orders();
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, productsRes, ordersRes, categoriesRes] = await Promise.all([
        seller.analytics(),
        seller.products({ page: 1, limit: 50 }),
        seller.orders(),
        categoriesApi.list()
      ]);
      setAnalytics(analyticsRes.data);
      // Handle both old array format and new paginated format
      const productsData = productsRes.data?.products || productsRes.data || [];
      setProducts(productsData);
      setTotalProducts(productsRes.data?.total || 0);
      setTotalPages(productsRes.data?.total_pages || 0);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convert price from selected currency to USD
  const convertToUSD = (price, currency) => {
    if (!price || !currency) return 0;
    return parseFloat(price) * exchangeRatesToUSD[currency];
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error(t('seller.enterCategoryName'));
      return;
    }

    try {
      const response = await seller.createCategory({
        name: newCategoryName.trim(),
        slug: newCategoryName.trim().toLowerCase().replace(/\s+/g, '-'),
        description: '',
        icon: null,
        parent_id: null
      });

      // Add new category to list
      setCategories([...categories, response.data]);
      
      // Select the new category automatically
      setNewProduct({ ...newProduct, category_id: response.data.id });
      
      toast.success(t('seller.categoryCreated'));
      setShowAddCategory(false);
      setNewCategoryName('');
    } catch (error) {
      console.error('Create category error:', error);
      toast.error(t('seller.categoryCreateFailed'));
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      // Convert prices to USD before saving
      const priceInUSD = convertToUSD(newProduct.price, selectedCurrency);
      const comparePriceInUSD = newProduct.compare_price 
        ? convertToUSD(newProduct.compare_price, selectedCurrency) 
        : null;

      // Determine country availability
      let availableCountriesData = ['ALL'];
      let excludedCountriesData = [];
      
      if (countryMode === 'include' && selectedCountries.length > 0) {
        availableCountriesData = selectedCountries;
      } else if (countryMode === 'exclude' && selectedCountries.length > 0) {
        availableCountriesData = ['ALL'];
        excludedCountriesData = selectedCountries;
      }

      const productData = {
        ...newProduct,
        slug: generateSlug(newProduct.name),
        price: priceInUSD,
        compare_price: comparePriceInUSD,
        tax_rate: newProduct.tax_rate ? parseFloat(newProduct.tax_rate) : null,
        shipping_cost: newProduct.shipping_cost ? parseFloat(newProduct.shipping_cost) : null,
        stock: parseInt(newProduct.stock),
        images: newProduct.images.filter(img => img.trim()),
        videos: newProduct.videos.filter(vid => vid.trim()),
        original_currency: selectedCurrency,
        original_price: parseFloat(newProduct.price),
        available_countries: availableCountriesData,
        excluded_countries: excludedCountriesData,
        // Physical properties
        weight: newProduct.weight ? parseFloat(newProduct.weight) : null,
        weight_unit: newProduct.weight_unit,
        dimensions: (newProduct.dimensions.width || newProduct.dimensions.height || newProduct.dimensions.depth) 
          ? {
              width: newProduct.dimensions.width ? parseFloat(newProduct.dimensions.width) : null,
              height: newProduct.dimensions.height ? parseFloat(newProduct.dimensions.height) : null,
              depth: newProduct.dimensions.depth ? parseFloat(newProduct.dimensions.depth) : null
            }
          : null,
        dimension_unit: newProduct.dimension_unit,
        volume: newProduct.volume ? parseFloat(newProduct.volume) : null,
        volume_unit: newProduct.volume_unit,
        // Product variants
        sizes: newProduct.sizes,
        colors: newProduct.colors,
        // Origin country
        origin_country: newProduct.origin_country || null,
        // Made in Türkiye badge
        made_in_turkey: newProduct.made_in_turkey || false,
        // Wholesale pricing tiers
        wholesale_tiers: newProduct.wholesale_tiers.map(tier => ({
          min_qty: parseInt(tier.min_qty),
          max_qty: tier.max_qty ? parseInt(tier.max_qty) : null,
          price: parseFloat(tier.price),
          unit: tier.unit
        }))
      };
      await productsApi.create(productData);
      toast.success(t('seller.productCreated'));
      toast.info(t('seller.autoTranslating'), { duration: 5000 });
      setShowAddProduct(false);
      setNewProduct({
        name: '',
        description: '',
        price: '',
        compare_price: '',
        tax_rate: '',
        shipping_cost: '',
        category_id: '',
        stock: '',
        brand: '',
        images: [''],
        videos: [''],
        is_featured: false,
        made_in_turkey: false,
        weight: '',
        weight_unit: 'kg',
        dimensions: { width: '', height: '', depth: '' },
        dimension_unit: 'cm',
        volume: '',
        volume_unit: 'L',
        sizes: [],
        colors: [],
        customColor: '',
        origin_country: '',
        wholesale_tiers: []
      });
      setSelectedCurrency('USD');
      setCountryMode('all');
      setSelectedCountries([]);
      fetchData();
    } catch (error) {
      toast.error(t('seller.productCreateFailed'));
    }
  };

  const handleCountryToggle = (countryCode) => {
    if (selectedCountries.includes(countryCode)) {
      setSelectedCountries(selectedCountries.filter(c => c !== countryCode));
    } else {
      setSelectedCountries([...selectedCountries, countryCode]);
    }
  };

  // Helper to convert YouTube/Vimeo URLs to embed URLs
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    // Direct video URL
    return url;
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(t('seller.confirmDeleteProduct'))) return;
    try {
      await productsApi.delete(productId);
      toast.success(t('seller.productDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('seller.productDeleteFailed'));
    }
  };

  const handleSaveEdit = async () => {
    if (!editingProduct) return;
    setEditSaving(true);
    try {
      const payload = buildProductUpdatePayload(editForm, editingProduct);
      if (Object.keys(payload).length === 0) {
        toast.info(t('seller.noChanges', 'Degisiklik yapilmadi'));
        return;
      }
      await api.patch(`/products/${editingProduct.id}`, payload);
      toast.success(t('seller.productUpdated'));
      setShowEditDialog(false);
      setEditingProduct(null);
      setEditForm({});
      fetchData();
    } catch (err) {
      toast.error(t('seller.updateFailed'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleExportXml = async () => {
    setExportingXml(true);
    try {
      const response = await seller.exportProductsXml();
      const blob = new Blob([response.data], { type: 'application/xml;charset=utf-8' });
      if (blob.size < 20) {
        toast.error(t('seller.exportXmlFailed', 'XML dışa aktarma başarısız'));
        return;
      }
      const disposition = response.headers?.['content-disposition'] || '';
      const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = decodeURIComponent(utfMatch?.[1] || plainMatch?.[1] || 'urunler.xml');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      const count = response.headers?.['x-export-count'];
      toast.success(
        count
          ? t('seller.exportXmlSuccessCount', '{{count}} ürün XML olarak indirildi', { count })
          : t('seller.exportXmlSuccess', 'Ürünler XML olarak indirildi')
      );
    } catch {
      toast.error(t('seller.exportXmlFailed', 'XML dışa aktarma başarısız'));
    } finally {
      setExportingXml(false);
    }
  };

  const handleCheckMarketPrice = async () => {
    if (!newProduct.name.trim()) {
      toast.error(t('seller.enterProductName'));
      return;
    }
    
    setMarketPriceLoading(true);
    setShowMarketPrice(true);
    
    try {
      const response = await seller.getMarketPrice(newProduct.name);
      setMarketPriceData(response.data);
    } catch (error) {
      toast.error(t('seller.fetchPriceFailed'));
      setMarketPriceData(null);
    } finally {
      setMarketPriceLoading(false);
    }
  };

  const stats = [
    { label: t('seller.totalRevenue'), value: formatDisplayPrice(analytics?.total_revenue || 0), icon: DollarSign, color: 'text-green-600' },
    { label: t('seller.totalTaxCollected'), value: formatDisplayPrice(analytics?.total_tax || 0), icon: DollarSign, color: 'text-yellow-600' },
    { label: t('seller.totalShippingIncome'), value: formatDisplayPrice(analytics?.total_shipping || 0), icon: Truck, color: 'text-cyan-600' },
    { label: t('seller.totalOrders'), value: analytics?.total_orders || 0, icon: ShoppingCart, color: 'text-blue-600' },
    { label: t('seller.products'), value: analytics?.total_products || 0, icon: Package, color: 'text-purple-600' },
    { label: t('seller.pendingOrders'), value: analytics?.pending_orders || 0, icon: TrendingUp, color: 'text-orange-600' }
  ];

  if (loading) {
    return (
      <div className="py-8">
        <div className="container mx-auto px-4 md:px-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="py-8" data-testid="seller-dashboard">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold">{t('seller.dashboard')}</h1>
            <p className="text-muted-foreground">{t('seller.manageProductsOrders')}</p>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full lg:w-auto lg:inline-flex flex-wrap gap-1">
            {hasAccessToTab('products') && (
              <TabsTrigger value="products" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                <span className="hidden sm:inline">{t('seller.products')}</span>
              </TabsTrigger>
            )}
            {hasAccessToTab('orders') && (
              <TabsTrigger value="orders" className="flex items-center gap-2" data-testid="seller-tab-orders">
                <ClipboardList className="w-4 h-4" />
                <span className="hidden sm:inline">{t('seller.ordersTab')}</span>
              </TabsTrigger>
            )}
            {/* TEM API — ürünler ve siparişlerden hemen sonra */}
            <TabsTrigger value="tem-api" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">TEM API</span>
            </TabsTrigger>
            {hasAccessToTab('car-rentals') && (
              <TabsTrigger value="car-rentals" className="flex items-center gap-2">
                <Car className="w-4 h-4" />
                <span className="hidden sm:inline">{t('seller.carRentals')}</span>
              </TabsTrigger>
            )}
            {hasAccessToTab('hotel-flights') && (
              <TabsTrigger value="hotel-flights" className="flex items-center gap-2">
                <Hotel className="w-4 h-4" />
                <Plane className="w-4 h-4" />
                <span className="hidden sm:inline">{t('seller.hotelFlights')}</span>
              </TabsTrigger>
            )}
            {hasAccessToTab('supermarket') && (
              <TabsTrigger value="supermarket" className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                <span className="hidden sm:inline">{t('seller.supermarket')}</span>
              </TabsTrigger>
            )}
            {hasAccessToTab('supermarket') && (
              <TabsTrigger value="marketplace" className="flex items-center gap-2">
                <Store className="w-4 h-4" />
                <span className="hidden sm:inline">{t('sellerPanel.marketplace.title')}</span>
              </TabsTrigger>
            )}
            {hasAccessToTab('restaurants') && (
              <TabsTrigger value="restaurants" className="flex items-center gap-2">
                <Utensils className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.restaurants')}</span>
              </TabsTrigger>
            )}
            {hasAccessToTab('taxi-limousine') && (
              <TabsTrigger value="taxi-limousine" className="flex items-center gap-2">
                <CarTaxiFront className="w-4 h-4" />
                <span className="hidden sm:inline">{t('headerNav.taxi')}</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Profit Margin Notice - Only show if user has profit margin set */}
          {user?.profit_margin_percent && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800">{t('seller.profitMarginTitle')}</h4>
                  <p className="text-amber-700 text-sm mt-1">
                    {t('seller.profitMarginBody', { percent: user.profit_margin_percent })}
                  </p>
                  {/* XML Source Info */}
                  {(user?.xml_source || user?.xml_import_date) && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        {user?.xml_source && (
                          <div className="flex items-center gap-2">
                            <Globe className="w-3 h-3 text-amber-600" />
                            <span className="text-amber-600">{t('seller.xmlSource')}</span>
                            <span className="text-amber-800 font-medium truncate max-w-[200px]" title={user.xml_source}>
                              {user.xml_source}
                            </span>
                          </div>
                        )}
                        {user?.xml_import_date && (
                          <div className="flex items-center gap-2">
                            <svg className="w-3 h-3 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-amber-600">{t('seller.lastUpdate')}</span>
                            <span className="text-amber-800 font-medium">
                              {new Date(user.xml_import_date).toLocaleDateString(i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <SellerStoreProfileCard langCode={(i18n.language || 'tr').substring(0, 2)} />
            {/* Translation Card */}
            <TranslationCard />
            
            <div className="flex justify-end gap-3 flex-wrap">
              <AIProductUpload />
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={handleExportXml}
                disabled={exportingXml}
                data-testid="export-products-xml-btn"
              >
                {exportingXml ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {t('seller.exportXml', 'Dışa Aktar (XML)')}
              </Button>
              <Link to="/seller/bulk-upload">
                <Button variant="outline" className="rounded-full" data-testid="bulk-upload-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {t('seller.bulkUpload')}
                </Button>
              </Link>
              <Dialog open={showAddProduct} onOpenChange={(open) => {
                setShowAddProduct(open);
                if (!open) {
                  setShowMarketPrice(false);
                  setMarketPriceData(null);
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('seller.addProduct')}
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('seller.addNewProduct')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddProduct} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">{t('seller.productName')}</Label>
                      <div className="flex gap-2">
                        <Input
                          id="name"
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                          required
                          className="flex-1"
                          data-testid="product-name-input"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          onClick={handleCheckMarketPrice}
                          title={t('seller.checkMarketPrice')}
                          data-testid="check-market-price-btn"
                        >
                          <Search className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="brand">{t('seller.brand')}</Label>
                      <Input
                        id="brand"
                        value={newProduct.brand}
                        onChange={(e) => setNewProduct({ ...newProduct, brand: e.target.value })}
                        data-testid="product-brand-input"
                      />
                    </div>
                  </div>

                  {/* Market Price Comparison Section - AI Powered */}
                  {showMarketPrice && (
                    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm font-medium text-blue-800 flex items-center gap-2">
                            <span>🤖</span>
                            {t('seller.aiMarketAnalysis')}
                          </CardTitle>
                          {marketPriceData?.ai_powered && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                              AI Powered
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {marketPriceLoading ? (
                          <div className="flex flex-col items-center justify-center py-6">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-2" />
                            <p className="text-sm text-blue-600">{t('seller.analyzingMarket')}</p>
                          </div>
                        ) : marketPriceData ? (
                          <>
                            {/* Price Summary */}
                            <div className="grid grid-cols-3 gap-4 text-center">
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs text-blue-600">{t('seller.minPrice')}</p>
                                <p className="text-xl font-bold text-green-600">
                                  ${marketPriceData.min_price?.toFixed(2) || 'N/A'}
                                </p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs text-blue-600">{t('seller.averagePrice')}</p>
                                <p className="text-xl font-bold text-blue-800">
                                  ${marketPriceData.avg_price?.toFixed(2) || 'N/A'}
                                </p>
                              </div>
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs text-blue-600">{t('seller.maxPrice')}</p>
                                <p className="text-xl font-bold text-orange-600">
                                  ${marketPriceData.max_price?.toFixed(2) || 'N/A'}
                                </p>
                              </div>
                            </div>

                            {/* AI Analysis */}
                            {marketPriceData.analysis && (
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs font-medium text-blue-700 mb-1">{t('seller.aiAnalysis')}</p>
                                <p className="text-sm text-gray-700">{marketPriceData.analysis}</p>
                              </div>
                            )}

                            {/* Competitors */}
                            {marketPriceData.competitors && marketPriceData.competitors.length > 0 && (
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs font-medium text-blue-700 mb-2">{t('seller.competitorPrices')}</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {marketPriceData.competitors.slice(0, 4).map((comp, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-sm bg-white/50 rounded px-2 py-1">
                                      <span className="text-gray-600">{comp.seller}</span>
                                      <span className="font-medium text-blue-700">
                                        ${(comp.estimated_price || comp.price)?.toFixed(2)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Pricing Advice */}
                            {marketPriceData.pricing_advice && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <p className="text-xs font-medium text-green-700 mb-1">💡 {t('seller.pricingAdvice')}</p>
                                <p className="text-sm text-green-800">{marketPriceData.pricing_advice}</p>
                              </div>
                            )}

                            {/* Confidence Level */}
                            {marketPriceData.confidence && (
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-xs text-gray-500">{t('seller.confidence')}:</span>
                                <Badge variant={
                                  marketPriceData.confidence === 'high' ? 'default' :
                                  marketPriceData.confidence === 'medium' ? 'secondary' : 'outline'
                                } className="text-xs">
                                  {marketPriceData.confidence === 'high' ? '🟢' : 
                                   marketPriceData.confidence === 'medium' ? '🟡' : '🔴'} {marketPriceData.confidence}
                                </Badge>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-blue-600 text-center">{t('seller.noMarketData')}</p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  <div>
                    <Label htmlFor="description">{t('seller.description')}</Label>
                    <Textarea
                      id="description"
                      value={newProduct.description}
                      onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                      required
                      data-testid="product-description-input"
                    />
                  </div>

                  {/* Currency Selection */}
                  <div>
                    <Label>{t('seller.priceCurrency')}</Label>
                    <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                      <SelectTrigger data-testid="currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(currencyLabels).map(([code, label]) => (
                          <SelectItem key={code} value={code}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('seller.currencyConversionNote')}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="price">{t('seller.price')} ({selectedCurrency})</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                        required
                        data-testid="product-price-input"
                      />
                      {newProduct.price && selectedCurrency !== 'USD' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ≈ ${convertToUSD(newProduct.price, selectedCurrency).toFixed(2)} USD
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="compare_price">{t('seller.comparePrice')} ({selectedCurrency})</Label>
                      <Input
                        id="compare_price"
                        type="number"
                        step="0.01"
                        value={newProduct.compare_price}
                        onChange={(e) => setNewProduct({ ...newProduct, compare_price: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tax_rate">{t('seller.taxRate')}</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="tax_rate"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={newProduct.tax_rate}
                          onChange={(e) => setNewProduct({ ...newProduct, tax_rate: e.target.value })}
                          placeholder="0"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{t('seller.taxRateDesc')}</p>
                    </div>
                    <div>
                      <Label htmlFor="shipping_cost">{t('seller.shippingCost')}</Label>
                      <Input
                        id="shipping_cost"
                        type="number"
                        step="0.01"
                        min="0"
                        value={newProduct.shipping_cost}
                        onChange={(e) => setNewProduct({ ...newProduct, shipping_cost: e.target.value })}
                        placeholder="0.00"
                      />
                      <p className="text-xs text-muted-foreground mt-1">{t('seller.shippingCostDesc')}</p>
                    </div>
                    <div>
                      <Label htmlFor="stock">{t('seller.stock')}</Label>
                      <Input
                        id="stock"
                        type="number"
                        value={newProduct.stock}
                        onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                        required
                        data-testid="product-stock-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="category">{t('seller.category')}</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={newProduct.category_id}
                        onValueChange={(value) => {
                          if (value === 'add_new') {
                            setShowAddCategory(true);
                          } else {
                            setNewProduct({ ...newProduct, category_id: value });
                          }
                        }}
                        required
                      >
                        <SelectTrigger id="category" className="flex-1">
                          <SelectValue placeholder={t('seller.selectCategory')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="add_new" className="text-primary font-medium">
                            <div className="flex items-center gap-2">
                              <Plus className="w-4 h-4" />
                              {t('seller.addNewCategory')}
                            </div>
                          </SelectItem>
                          <Separator className="my-1" />
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {t(`categories.${cat.slug}`) || cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Image Upload Section */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-blue-600" />
                      <Label className="text-base font-medium">{t('seller.productImage')}</Label>
                    </div>
                    
                    {/* File Upload */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">{t('seller.uploadFromComputer')}</Label>
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error(t('store.logoTooLarge'));
                                return;
                              }
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/upload-product-image`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  },
                                  body: formData
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setNewProduct({ ...newProduct, images: [data.url] });
                                  toast.success(t('seller.imageUploaded'));
                                } else {
                                  const error = await response.json();
                                  toast.error(error.detail || t('seller.imageUploadFailed'));
                                }
                              } catch (error) {
                                console.error('Upload error:', error);
                                toast.error(t('seller.imageUploadError'));
                              }
                            }
                          }}
                          className="flex-1"
                          data-testid="product-image-upload"
                        />
                      </div>
                    </div>
                    
                    {/* URL Input */}
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">{t('seller.orPasteUrl')}</Label>
                      <Input
                        id="image"
                        value={newProduct.images[0]}
                        onChange={(e) => setNewProduct({ ...newProduct, images: [e.target.value] })}
                        placeholder="https://example.com/image.jpg"
                        data-testid="product-image-input"
                      />
                    </div>
                    
                    {/* Image Preview */}
                    {newProduct.images[0] && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-2">{t('seller.preview')}</p>
                        <div className="relative w-32 h-32">
                          <img
                            src={newProduct.images[0]?.startsWith('/uploads/') ? newProduct.images[0].replace('/uploads/', '/api/uploads/') : newProduct.images[0]}
                            alt="Preview"
                            className="w-full h-full object-cover rounded-lg border"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => setNewProduct({ ...newProduct, images: [''] })}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Video URL Section */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Video className="w-5 h-5 text-purple-600" />
                      <Label className="text-base font-medium">{t('seller.productVideo')}</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t('seller.videoUrlDescription')}
                    </p>
                    <Input
                      value={newProduct.videos[0]}
                      onChange={(e) => setNewProduct({ ...newProduct, videos: [e.target.value] })}
                      placeholder="https://www.youtube.com/watch?v=... veya https://vimeo.com/..."
                      data-testid="product-video-input"
                    />
                    {newProduct.videos[0] && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-2">{t('seller.videoPreview')}</p>
                        <div className="aspect-video bg-black rounded-lg overflow-hidden">
                          {newProduct.videos[0].includes('youtube') || newProduct.videos[0].includes('youtu.be') ? (
                            <iframe
                              src={getVideoEmbedUrl(newProduct.videos[0])}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="Video Preview"
                            />
                          ) : newProduct.videos[0].includes('vimeo') ? (
                            <iframe
                              src={getVideoEmbedUrl(newProduct.videos[0])}
                              className="w-full h-full"
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                              title="Video Preview"
                            />
                          ) : (
                            <video
                              src={newProduct.videos[0]}
                              controls
                              className="w-full h-full"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Country Availability Section */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-600" />
                      <Label className="text-base font-medium">{t('seller.countryAvailability')}</Label>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={countryMode === 'all' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => { setCountryMode('all'); setSelectedCountries([]); }}
                          className="flex-1"
                        >
                          🌍 {t('seller.worldwideAvailable')}
                        </Button>
                        <Button
                          type="button"
                          variant={countryMode === 'include' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCountryMode('include')}
                          className="flex-1"
                        >
                          ✅ {t('seller.selectedCountriesOnly')}
                        </Button>
                        <Button
                          type="button"
                          variant={countryMode === 'exclude' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCountryMode('exclude')}
                          className="flex-1"
                        >
                          ❌ {t('seller.excludeCountries')}
                        </Button>
                      </div>

                      {countryMode !== 'all' && (
                        <>
                          <p className="text-sm text-muted-foreground">
                            {countryMode === 'include' 
                              ? t('seller.selectCountriesToInclude')
                              : t('seller.selectCountriesToExclude')}
                          </p>
                          
                          {/* Selected Countries */}
                          {selectedCountries.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {selectedCountries.map(code => {
                                const country = availableCountries.find(c => c.code === code);
                                return (
                                  <Badge 
                                    key={code} 
                                    variant="secondary" 
                                    className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                                    onClick={() => handleCountryToggle(code)}
                                  >
                                    {country?.flag} {country?.name} <X className="w-3 h-3 ml-1" />
                                  </Badge>
                                );
                              })}
                            </div>
                          )}

                          {/* Country Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                            {availableCountries.filter(c => c.code !== 'ALL').map((country) => (
                              <div
                                key={country.code}
                                className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-secondary transition-colors ${
                                  selectedCountries.includes(country.code) 
                                    ? 'bg-primary/10 border border-primary' 
                                    : 'bg-secondary/30'
                                }`}
                                onClick={() => handleCountryToggle(country.code)}
                              >
                                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                  selectedCountries.includes(country.code)
                                    ? 'bg-primary border-primary'
                                    : 'border-gray-300'
                                }`}>
                                  {selectedCountries.includes(country.code) && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <span className="text-lg">{country.flag}</span>
                                <span className="text-sm truncate">{country.name}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {countryMode === 'all' && (
                        <p className="text-sm text-green-600 bg-green-50 p-2 rounded">
                          ✅ {t('seller.productAvailableWorldwide')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Physical Properties Section */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <Label className="text-base font-medium">📦 {t('seller.physicalProperties')}</Label>
                    <p className="text-sm text-muted-foreground">{t('seller.physicalPropertiesDesc')}</p>
                    
                    {/* Weight */}
                    <div className="grid md:grid-cols-4 gap-2">
                      <div className="md:col-span-3">
                        <Label htmlFor="weight">{t('seller.weight')}</Label>
                        <Input
                          id="weight"
                          type="number"
                          step="0.01"
                          value={newProduct.weight}
                          onChange={(e) => setNewProduct({ ...newProduct, weight: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="weight-unit">{t('seller.unit')}</Label>
                        <Select 
                          value={newProduct.weight_unit}
                          onValueChange={(value) => setNewProduct({ ...newProduct, weight_unit: value })}
                        >
                          <SelectTrigger id="weight-unit">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="g">g</SelectItem>
                            <SelectItem value="lb">lb</SelectItem>
                            <SelectItem value="oz">oz</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Dimensions */}
                    <div>
                      <Label>{t('seller.dimensions')}</Label>
                      <div className="grid md:grid-cols-4 gap-2 mt-2">
                        <Input
                          type="number"
                          step="0.1"
                          value={newProduct.dimensions.width}
                          onChange={(e) => setNewProduct({ 
                            ...newProduct, 
                            dimensions: { ...newProduct.dimensions, width: e.target.value }
                          })}
                          placeholder={t('seller.width')}
                        />
                        <Input
                          type="number"
                          step="0.1"
                          value={newProduct.dimensions.height}
                          onChange={(e) => setNewProduct({ 
                            ...newProduct, 
                            dimensions: { ...newProduct.dimensions, height: e.target.value }
                          })}
                          placeholder={t('seller.height')}
                        />
                        <Input
                          type="number"
                          step="0.1"
                          value={newProduct.dimensions.depth}
                          onChange={(e) => setNewProduct({ 
                            ...newProduct, 
                            dimensions: { ...newProduct.dimensions, depth: e.target.value }
                          })}
                          placeholder={t('seller.depth')}
                        />
                        <Select 
                          value={newProduct.dimension_unit}
                          onValueChange={(value) => setNewProduct({ ...newProduct, dimension_unit: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cm">cm</SelectItem>
                            <SelectItem value="inch">inch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Volume */}
                    <div className="grid md:grid-cols-4 gap-2">
                      <div className="md:col-span-3">
                        <Label htmlFor="volume">{t('seller.volume')}</Label>
                        <Input
                          id="volume"
                          type="number"
                          step="0.01"
                          value={newProduct.volume}
                          onChange={(e) => setNewProduct({ ...newProduct, volume: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="volume-unit">{t('seller.unit')}</Label>
                        <Select 
                          value={newProduct.volume_unit}
                          onValueChange={(value) => setNewProduct({ ...newProduct, volume_unit: value })}
                        >
                          <SelectTrigger id="volume-unit">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="L">L</SelectItem>
                            <SelectItem value="mL">mL</SelectItem>
                            <SelectItem value="m³">m³</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Product Variants Section */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <Label className="text-base font-medium">👕 {t('seller.productVariants')}</Label>
                    <p className="text-sm text-muted-foreground">{t('seller.productVariantsDesc')}</p>
                    
                    {/* Sizes */}
                    <div>
                      <Label>{t('seller.sizes')}</Label>
                      <p className="text-xs text-muted-foreground mb-2">{t('seller.sizesDesc')}</p>
                      <div className="flex flex-wrap gap-2 p-3 bg-secondary/20 rounded-lg">
                        {availableSizes.map(size => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => {
                              if (newProduct.sizes.includes(size)) {
                                setNewProduct({ 
                                  ...newProduct, 
                                  sizes: newProduct.sizes.filter(s => s !== size) 
                                });
                              } else {
                                setNewProduct({ 
                                  ...newProduct, 
                                  sizes: [...newProduct.sizes, size] 
                                });
                              }
                            }}
                            className={`px-3 py-1 rounded-md text-sm transition-colors ${
                              newProduct.sizes.includes(size)
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-background hover:bg-secondary'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                      {newProduct.sizes.length > 0 && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {t('seller.selectedSizes')}: {newProduct.sizes.join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Colors */}
                    <div>
                      <Label>{t('seller.colors')}</Label>
                      <p className="text-xs text-muted-foreground mb-2">{t('seller.colorsDesc')}</p>
                      <div className="space-y-3">
                        {/* Predefined Colors */}
                        <div className="flex flex-wrap gap-2 p-3 bg-secondary/20 rounded-lg">
                          {predefinedColors.map(color => (
                            <button
                              key={color.name}
                              type="button"
                              onClick={() => {
                                const colorValue = `${color.name} (${color.value})`;
                                if (newProduct.colors.includes(colorValue)) {
                                  setNewProduct({ 
                                    ...newProduct, 
                                    colors: newProduct.colors.filter(c => c !== colorValue) 
                                  });
                                } else {
                                  setNewProduct({ 
                                    ...newProduct, 
                                    colors: [...newProduct.colors, colorValue] 
                                  });
                                }
                              }}
                              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors border-2 ${
                                newProduct.colors.some(c => c.startsWith(color.name))
                                  ? 'border-primary bg-primary/10'
                                  : 'border-transparent bg-background hover:bg-secondary'
                              }`}
                              title={color.value}
                            >
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-300" 
                                style={{ backgroundColor: color.value }}
                              />
                              {color.name}
                            </button>
                          ))}
                        </div>
                        
                        {/* Custom Color Input */}
                        <div className="flex gap-2">
                          <Input
                            value={newProduct.customColor}
                            onChange={(e) => setNewProduct({ ...newProduct, customColor: e.target.value })}
                            placeholder={t('seller.customColorPlaceholder')}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              if (newProduct.customColor.trim()) {
                                setNewProduct({
                                  ...newProduct,
                                  colors: [...newProduct.colors, newProduct.customColor.trim()],
                                  customColor: ''
                                });
                              }
                            }}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Selected Colors */}
                        {newProduct.colors.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {newProduct.colors.map((color, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-secondary rounded-md text-xs"
                              >
                                {color}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewProduct({
                                      ...newProduct,
                                      colors: newProduct.colors.filter((_, i) => i !== index)
                                    });
                                  }}
                                  className="hover:text-destructive"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Origin Country Section */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <Label className="text-base font-medium">🏭 {t('seller.originCountry')}</Label>
                    <p className="text-sm text-muted-foreground">{t('seller.originCountryDesc')}</p>
                    <Select 
                      value={newProduct.origin_country}
                      onValueChange={(value) => setNewProduct({ ...newProduct, origin_country: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('seller.selectOriginCountry')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {availableCountries.filter(c => c.code !== 'ALL').map(country => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Made in Türkiye Badge */}
                  <div className="border rounded-lg p-4 space-y-3 bg-red-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_emarket-turkey-1/artifacts/dupde9ke_madeinturkiye%20logo%20.png"
                          alt="Made in Türkiye"
                          className="w-10 h-10 object-contain"
                        />
                        <div>
                          <Label className="text-base font-medium">{t('seller.madeInTurkeyBadge')}</Label>
                          <p className="text-sm text-muted-foreground">{t('seller.madeInTurkeyDesc')}</p>
                        </div>
                      </div>
                      <Switch
                        checked={newProduct.made_in_turkey}
                        onCheckedChange={(checked) => setNewProduct({ ...newProduct, made_in_turkey: checked })}
                      />
                    </div>
                    {newProduct.made_in_turkey && (
                      <p className="text-xs text-green-700 bg-green-100 p-2 rounded">
                        ✓ {t('seller.madeInTurkeyShown')}
                      </p>
                    )}
                  </div>

                  {/* Wholesale Pricing Section */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-medium">💰 {t('seller.wholesalePricing')}</Label>
                        <p className="text-sm text-muted-foreground">{t('seller.wholesalePricingDesc')}</p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setNewProduct({
                            ...newProduct,
                            wholesale_tiers: [...newProduct.wholesale_tiers, { min_qty: '', max_qty: '', price: '', unit: 'item' }]
                          });
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('seller.addTier')}
                      </Button>
                    </div>

                    {newProduct.wholesale_tiers.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4 bg-secondary/20 rounded-lg">
                        {t('seller.noWholesaleTiers')}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {newProduct.wholesale_tiers.map((tier, index) => (
                          <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-secondary/20 rounded-lg">
                            <div className="col-span-3">
                              <Label className="text-xs">{t('seller.minQuantity')}</Label>
                              <Input
                                type="number"
                                min="1"
                                value={tier.min_qty}
                                onChange={(e) => {
                                  const newTiers = [...newProduct.wholesale_tiers];
                                  newTiers[index].min_qty = e.target.value;
                                  setNewProduct({ ...newProduct, wholesale_tiers: newTiers });
                                }}
                                placeholder="10"
                              />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs">{t('seller.maxQuantity')}</Label>
                              <Input
                                type="number"
                                min="1"
                                value={tier.max_qty}
                                onChange={(e) => {
                                  const newTiers = [...newProduct.wholesale_tiers];
                                  newTiers[index].max_qty = e.target.value;
                                  setNewProduct({ ...newProduct, wholesale_tiers: newTiers });
                                }}
                                placeholder={t('seller.unlimited')}
                              />
                            </div>
                            <div className="col-span-3">
                              <Label className="text-xs">{t('seller.pricePerUnit')}</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={tier.price}
                                onChange={(e) => {
                                  const newTiers = [...newProduct.wholesale_tiers];
                                  newTiers[index].price = e.target.value;
                                  setNewProduct({ ...newProduct, wholesale_tiers: newTiers });
                                }}
                                placeholder="8.50"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">{t('seller.unitType')}</Label>
                              <Select 
                                value={tier.unit}
                                onValueChange={(value) => {
                                  const newTiers = [...newProduct.wholesale_tiers];
                                  newTiers[index].unit = value;
                                  setNewProduct({ ...newProduct, wholesale_tiers: newTiers });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="item">{t('seller.perItem')}</SelectItem>
                                  <SelectItem value="kg">{t('seller.perKg')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                  setNewProduct({
                                    ...newProduct,
                                    wholesale_tiers: newProduct.wholesale_tiers.filter((_, i) => i !== index)
                                  });
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {newProduct.wholesale_tiers.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        💡 {t('seller.wholesaleTip')}
                      </p>
                    )}
                  </div>

                  <Button type="submit" className="w-full rounded-full" data-testid="submit-product-btn">
                    {t('seller.createProduct')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

        {/* Stats */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-full bg-secondary ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Products table */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('seller.myProducts')}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-4">
              <Input
                type="search"
                placeholder={t('seller.searchProductsPlaceholder')}
                value={productSearchQuery}
                onChange={(e) => { setProductSearchQuery(e.target.value); setProductPage(1); }}
                className="max-w-md"
                data-testid="seller-product-search"
              />
            </div>
            {products.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">{t('seller.noProducts')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('seller.product')}</TableHead>
                    <TableHead>{t('seller.price')}</TableHead>
                    <TableHead>{t('seller.stock')}</TableHead>
                    <TableHead>{t('seller.status')}</TableHead>
                    <TableHead className="text-right">{t('seller.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary">
                            <img
                              src={(() => { const u = product.images?.[0] || '/placeholder.jpg'; return u.startsWith('/uploads/') ? u.replace('/uploads/', '/api/uploads/') : u; })()}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{product.name}</p>
                              {product.translations && Object.keys(product.translations).length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  🌐 {Object.keys(product.translations).length} {t('seller.languages')}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{product.brand}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{formatDisplayPrice(product.price)}</TableCell>
                      <TableCell>
                        <Badge variant={product.stock > 10 ? 'secondary' : product.stock > 0 ? 'outline' : 'destructive'}>
                          {product.stock} {t('seller.inStock')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={product.is_active ? 'default' : 'secondary'}>
                          {product.is_active ? t('seller.active') : t('seller.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={buildProductPath('tr', product)}>
                                <Eye className="w-4 h-4 mr-2" /> {t('seller.view')}
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.preventDefault();
                              setEditingProduct(product);
                              setEditForm(initProductEditForm(product));
                              setShowEditDialog(true);
                            }}>
                              <Edit2 className="w-4 h-4 mr-2" /> {t('seller.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> {t('seller.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            <SmartPagination
              currentPage={productPage}
              totalPages={totalPages}
              totalItems={totalProducts}
              pageSize={50}
              onPageChange={(p) => setProductPage(p)}
            />
          </CardContent>
        </Card>

        {/* Edit Product Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="seller-product-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-primary" />
                {t('seller.editProduct')}
              </DialogTitle>
            </DialogHeader>
            {editingProduct && (
              <div className="space-y-4">
                <ProductDetailEditForm
                  form={editForm}
                  setForm={setEditForm}
                  product={editingProduct}
                  categoryOptions={Array.isArray(categories) ? categories : []}
                  imageUploadTestId="edit-image-upload"
                />
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowEditDialog(false)}>{t('seller.cancel')}</Button>
                  <Button onClick={handleSaveEdit} disabled={editSaving} data-testid="save-edit-btn">
                    {editSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('seller.save')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Add Category Dialog */}
        <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                {t('seller.addNewCategory')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="categoryName">{t('seller.categoryName')}</Label>
                <Input
                  id="categoryName"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder={t('seller.enterCategoryName')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateCategory();
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowAddCategory(false);
                  setNewCategoryName('');
                }}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreateCategory} disabled={!newCategoryName.trim()}>
                  {t('seller.createCategory')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
          </TabsContent>

          {/* Marketplace siparişleri — admin paneliyle aynı detay / düzenleme */}
          {hasAccessToTab('orders') && (
            <TabsContent value="orders">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" />
                    {t('seller.ordersTab')}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => refreshSellerOrders()}
                    disabled={ordersLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${ordersLoading ? 'animate-spin' : ''}`} />
                    {t('common.refresh', 'Yenile')}
                  </Button>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                      <p className="text-muted-foreground">{t('seller.noOrders')}</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('seller.orderId')}</TableHead>
                          <TableHead>{t('seller.customer')}</TableHead>
                          <TableHead>{t('seller.items')}</TableHead>
                          <TableHead>{t('seller.total')}</TableHead>
                          <TableHead>{t('seller.status')}</TableHead>
                          <TableHead>{t('checkout.paymentMethod')}</TableHead>
                          <TableHead>{t('seller.date')}</TableHead>
                          <TableHead title={t('seller.parasutColumnTitle')}>{t('seller.parasutColumn')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow
                            key={order.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => {
                              setSellerOrderDetailId(order.id);
                              setSellerOrderDetailOpen(true);
                            }}
                          >
                            <TableCell className="font-mono text-sm">
                              #{order.order_number || order.id?.slice(0, 8)}
                            </TableCell>
                            <TableCell>{order.shipping_address?.full_name}</TableCell>
                            <TableCell>
                              {(order.items?.length ?? 0)} {t('seller.itemCount')}
                            </TableCell>
                            <TableCell>{formatDisplayPrice(order.total)}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="capitalize">{order.status}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatPaymentMethodLabel(resolveOrderPaymentMethod(order), i18n.language?.split('-')[0] || 'tr')}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{formatDate(order.created_at)}</TableCell>
                            <TableCell className="whitespace-nowrap">
                              {order.parasut_sales_invoice_id ? (
                                <span className="inline-flex items-center gap-1 text-emerald-700" title={`ID: ${order.parasut_sales_invoice_id}`}>
                                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                                  <span className="text-xs font-medium">{t('seller.parasutSynced')}</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-muted-foreground" title={t('seller.parasutPending')}>
                                  <Minus className="w-4 h-4 shrink-0" />
                                  <span className="text-xs">—</span>
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Car Rentals Tab */}
          <TabsContent value="car-rentals">
            <CarRentalTab />
          </TabsContent>

          {/* Hotel & Flights Tab */}
          <TabsContent value="hotel-flights">
            <HotelFlightTab />
          </TabsContent>
          
          {/* Supermarket Tab */}
          <TabsContent value="supermarket">
            <SupermarketTab />
          </TabsContent>
          
          {/* Marketplace Integration Tab */}
          <TabsContent value="marketplace">
            <MarketplaceIntegrationTab />
          </TabsContent>
          
          <TabsContent value="restaurants">
            <RestaurantTab />
          </TabsContent>
          
          {/* Taxi & Limousine Tab */}
          <TabsContent value="taxi-limousine">
            <TaxiLimousineTab />
          </TabsContent>

          {/* TEM API Tab */}
          <TabsContent value="tem-api">
            <TEMApiTab />
          </TabsContent>
        </Tabs>
      </div>
      
      <OrderDetailModal
        orderId={sellerOrderDetailId}
        open={sellerOrderDetailOpen}
        onClose={() => {
          setSellerOrderDetailOpen(false);
          setSellerOrderDetailId(null);
        }}
        onUpdate={() => refreshSellerOrders()}
        variant="seller"
      />

      {/* AI Product Upload Chatbox */}
      <ProductAIChatbox isApproved={user?.seller_status === 'approved' || user?.role === 'admin'} />
    </div>
  );
};

export default SellerDashboardPage;
